import type { PowerSyncBackendConnector } from "@powersync/common";
import { flushGuestScratch, type GuestFlushOptions } from "@fleet/powersync";

/**
 * Guest → account drain that survives a reload.
 *
 * `flushGuestDataToServer` (power-sync-context.tsx) uploads the guest scratch's
 * queued writes as the real account. If the page reloads before that queue
 * drains, the rows stay in the guest DB's `ps_crud` and nothing retries them
 * at boot, because the session is already the real account. This marker records
 * the destination userId while the drain is pending, so boot can resume it as
 * that same account.
 */

export const PENDING_GUEST_FLUSH_KEY = "pending_guest_flush";

type MarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): MarkerStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function writePendingGuestFlush(userId: string, storage = defaultStorage()): void {
  try {
    storage?.setItem(PENDING_GUEST_FLUSH_KEY, userId);
  } catch {
    // Storage can be blocked (private mode). The drain still runs this session.
  }
}

export function readPendingGuestFlush(storage = defaultStorage()): string | null {
  try {
    return storage?.getItem(PENDING_GUEST_FLUSH_KEY) ?? null;
  } catch {
    return null;
  }
}

export function clearPendingGuestFlush(storage = defaultStorage()): void {
  try {
    storage?.removeItem(PENDING_GUEST_FLUSH_KEY);
  } catch {
    // Nothing else to do: a stale marker only costs one empty check at boot.
  }
}

export type ResumeOutcome = "none" | "empty" | "resumed";

/**
 * Boot-time retry. `userId` is the marker read at boot (`null` = no drain was
 * pending). `empty` means the guest queue is already drained, so the caller
 * clears the marker. `resumed` means `flush` ran as the destination account.
 */
export async function resumePendingGuestFlush(deps: {
  userId: string | null;
  pendingCount: () => Promise<number>;
  flush: (userId: string) => Promise<void>;
}): Promise<ResumeOutcome> {
  if (deps.userId === null) {
    return "none";
  }
  if ((await deps.pendingCount()) === 0) {
    return "empty";
  }
  await deps.flush(deps.userId);
  return "resumed";
}

/** The part of the guest scratch DB the drain uses. The real `AbstractPowerSyncDatabase` satisfies it. */
export interface GuestScratch {
  connect(connector: PowerSyncBackendConnector): Promise<void>;
  disconnect(): Promise<void>;
  disconnectAndClear(): Promise<void>;
  getAll(sql: string): Promise<unknown[]>;
}

/** Reads `SELECT count(*) AS n` without a cast. Anything unexpected counts as not drained. */
function queuedCount(row: unknown): number {
  if (typeof row === "object" && row !== null && "n" in row && typeof row.n === "number") {
    return row.n;
  }
  return -1;
}

/**
 * `none`: no guest scratch exists, nothing was written. `drained`: the upload
 * queue is empty (or wiping failed after a full upload), the marker is cleared
 * and the caller may forget the scratch. `kept`: the queue did not drain, the
 * scratch and the marker stay for the next boot.
 */
export type GuestDrainOutcome = "none" | "drained" | "kept";

/**
 * Uploads the guest scratch's queued writes as `realUserId`. The marker is
 * written first, so a reload mid-drain resumes as the same account.
 */
export async function drainGuestScratch(deps: {
  scratch: GuestScratch | undefined;
  realUserId: string;
  /** A connector namespaced to the real account (its own token cache). */
  connectorFor: (userId: string) => PowerSyncBackendConnector;
  options?: GuestFlushOptions;
}): Promise<GuestDrainOutcome> {
  const { scratch, realUserId } = deps;
  if (!scratch) {
    return "none";
  }
  writePendingGuestFlush(realUserId);
  try {
    await scratch.connect(deps.connectorFor(realUserId));
  } catch (err) {
    // Not fatal: the writes stay queued, and the flush below keeps them.
    console.warn("[PowerSyncProvider] guest connect failed:", err);
  }
  // The scratch is cleared only once its upload queue is empty; otherwise its
  // rows stay put and the next sign-in upgrade retries them (`@fleet/powersync`).
  const outcome = await flushGuestScratch(
    {
      pendingCount: async () => {
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- SQL query
        const rows = await scratch.getAll("SELECT count(*) AS n FROM ps_crud");
        return queuedCount(rows[0]);
      },
      clear: () => scratch.disconnectAndClear(),
      disconnect: () => scratch.disconnect(),
    },
    deps.options,
  );
  if (outcome === "kept") {
    return "kept";
  }
  clearPendingGuestFlush();
  return "drained";
}
